/**
 * OutdoorTemplateSelector - Template selection UI for outdoor areas
 *
 * Displays available outdoor area templates as cards.
 * Currently only Rectangular Area is available; Row, Grid, and Polygon are "Coming Soon".
 */

import { outdoorTemplateInfo, type TemplateType } from '@/lib/templates'
import { cn } from '@/lib/utils'

interface OutdoorTemplateSelectorProps {
  onSelect: (templateType: TemplateType) => void | Promise<void>
}

export function OutdoorTemplateSelector({ onSelect }: OutdoorTemplateSelectorProps) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold text-lg">Select Outdoor Template</h3>
        <p className="text-sm text-muted-foreground">
          Choose a template for the outdoor area
        </p>
      </div>

      <div className="space-y-2">
        {outdoorTemplateInfo.map((template) => {
          const Icon = template.icon
          const isAvailable = template.available

          return (
            <button
              key={template.id}
              onClick={() => isAvailable && onSelect(template.id)}
              disabled={!isAvailable}
              className={cn(
                'w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-colors',
                isAvailable
                  ? 'hover:bg-accent hover:border-primary cursor-pointer'
                  : 'opacity-50 cursor-not-allowed bg-muted/50'
              )}
            >
              <div
                className={cn(
                  'flex-shrink-0 p-2 rounded-md',
                  isAvailable ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                )}
              >
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{template.name}</span>
                  {!isAvailable && (
                    <span className="text-xs bg-muted px-1.5 py-0.5 rounded">Coming Soon</span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{template.description}</p>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
